import * as React from "react"
import styled from "@emotion/styled"
import Button, { ButtonBase } from "components/button"
import { css } from "@emotion/react"
import { theme } from "common/colorScheme"
import { mediaBreakpoint, BreakPoints } from "settings/breakpoints"
import * as hookform from "@hookform/resolvers"
import { useForm } from "react-hook-form"
import { SuccessIcon } from "components/icons"
import { SectionThemeContext } from "components/sections/SectionThemeContext"
import { elementData } from "components/sections/utils"
import { H5, P } from "components/headings"
import * as yup from "yup"
import { generateDefaultValues, generateFieldValidation } from "./utils"
import FormField, { FieldTypes, FormFieldProps } from "./FormField"
import Checkbox from "./Checkbox"
import useFormSubmit from "./hooks/useFormSubmit"
import { ArticleContent } from '../../templates/articleTemplate'

const FormBase = styled.form`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	background: ${props => props.theme.gradient};
	border-radius: 8px;
	padding: ${theme.spacing(3.5, 5)};
	color: ${theme.color.white};
	margin: ${theme.spacing(0, 0, 6)};

	position: relative;
	${H5} {
		color: ${theme.color.white};
	}
	${P} {
		color: ${theme.color.white};
		font-size: 1.6rem;
		line-height: 2rem;
		margin: 0;
	}
	${mediaBreakpoint(BreakPoints.SM)} {
		${H5} {
			font-size: 2.8rem;
		}
		box-shadow: 7px 7px 18px 0 rgba(35, 35, 35, 0.1);
	}
	${mediaBreakpoint(BreakPoints.MD)} {
		flex-direction: row;
	}
	${ButtonBase}[data-variant="primary"] {
		background: rgba(255, 255, 255, 1);
		color: var(--main-color);
		&:hover {
			background: rgba(255, 255, 255, 0.9) !important;
		}
	}
	&[aria-busy="true"] {
		pointer-events: none;
		&:before {
			content: "";
			display: block;
			position: absolute;
			width: 100%;
			height: 100%;
			top: 0;
			left: 0;
			background: var(--main-color);
			opacity: 0.3;
		}
	}
	${mediaBreakpoint(BreakPoints.SM)} {
		align-items: center;
	}
`

const FormLeft = styled.div`
	width: 100%;
	max-width: 220px;
`

const FormTitle = styled.h3`
	font-weight: 800;
	font-size: 2em;
	margin-bottom: 0.3em;

	${mediaBreakpoint(BreakPoints.MD)} {
		font-size: 3.2em;
	}
`

const FormCenter = styled.div`
	width: 100%;
	margin: ${theme.spacing(0, 0, 4)};
	${mediaBreakpoint(BreakPoints.MD)} {
		margin: 0;
	}
`

const FormRight = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;
	max-width: 220px;
`

const FormRightContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;

	${mediaBreakpoint(BreakPoints.XS)} {
		align-items: center;
	}
	${mediaBreakpoint(BreakPoints.MD)} {
		align-items: flex-start;
	}
`

const PrivacyConsent = styled.span`
	display: block;
	font-size: 1.6rem;
	font-weight: normal;
	line-height: 2rem;
	color: #fff;
	margin: 0 0 4px;
	> a {
		color: inherit;
		font-weight: 800;
	}
	${mediaBreakpoint(BreakPoints.SM)} {
		line-height: 2.8rem;
	}
`

const ErrorLabel = styled.div`
	height: 1em;
	span {
		font-size: 1.2rem;
		color: var(--error-color);
		margin: 0 0 3px;
	}
`

const MessageOverlay = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	z-index: 3;
	position: absolute;
	background: linear-gradient(41deg, #371074 0%, #7122da 100%);
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	padding: 32px;
`

const MessageOverlaySeparator = styled.div`
	width: 100%;
	height: 1px;
	background: #fff;
	margin: 32px 0 28px;
`

const MessageOverlayText = styled.div`
	max-width: 330px;
	font-size: 2rem;
	line-height: 2.8rem;
	text-align: center;
	a {
		color: #fff;
		font-weight: bold;
	}
`

const MessageOverlayIcon = styled.div`
	width: 78px;
	height: 78px;
	background: url("${SuccessIcon}") no-repeat center center;
`

const ImageWrapper = styled.div`
	margin: 0 auto;
	max-width: 186px;
`
const customInputStyles = css`
	margin: 0 0 8px;
	width: 100%;
	${mediaBreakpoint(BreakPoints.SM)} {
		margin: 0 0 4px;
	}
	input {
		max-height: 48px;
	}
`

const customButtonStyles = css`
	overflow: unset;
	width: 100% !important;
	&:hover {
		background: rgba(255, 255, 255, 0.9) !important;
	}
`

export interface FormProps {
	id?: string
	formId?: string
	hubspotFormId?: string
	contentfulfields?: FormFieldProps[]
	privacyConsent?: boolean | null
	submitText?: string
	bannerTitle?: string,
	bannerSubtitle?: string
	bannerImage?: any
	bannerGradient?: string
	html: any
	bannerForm: any
}

interface SubmitMessage {
	type: "success" | "error"
	message: string
}

const BlogPost: React.FC<FormProps> = ({ bannerForm = {}, html }) => {
	const PRIVACY_AND_POLICY = `I accept the<a href="https://www.latana.com/legal-terms/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>`
	const [submitedMessage, setSubittedMessage] = React.useState<SubmitMessage>()
	const [pending, setPending] = React.useState(false)

	const schema = yup.object({
		firstname: generateFieldValidation({
			type: FieldTypes.TextField,
			fieldName: "firstname",
			required: true,
			message: "Please enter your first name",
		}),
		lastname: generateFieldValidation({
			type: FieldTypes.TextField,
			fieldName: "lastname",
			required: true,
			message: "Please enter your last name",
		}),
		email: generateFieldValidation({
			type: FieldTypes.EmailField,
			fieldName: "email",
			required: true,
			message: "Please enter a valid email address",
		}),
		termsAccepted: yup.boolean().oneOf([true], "Please mark the checkbox"),
	})

	const defaultValues = React.useMemo(() => {
		return generateDefaultValues(bannerForm?.contentfulfields)
	}, [bannerForm?.contentfulfields])

	const { register, handleSubmit, errors, setError } = useForm({
		defaultValues,
		resolver: hookform.yupResolver(schema),
	})

	const onSubmit = useFormSubmit(setPending, setSubittedMessage, setError, bannerForm?.formId, bannerForm?.hubspotFormId, bannerForm?.contentfulfields)

	return (
		<>
			<ArticleContent>
				{html}
			</ArticleContent>
			{bannerForm.formId ? (
				<SectionThemeContext.Provider value="default">
					<FormBase
						id={bannerForm?.formId}
						onSubmit={handleSubmit(onSubmit)}
						data-element={elementData.formContainer}
						aria-busy={pending}
						theme={{ ...theme, gradient: bannerForm?.bannerGradient }}
					>
						<FormLeft>
							<FormTitle>{bannerForm?.bannerTitle}</FormTitle>
							<P>{bannerForm?.bannerSubtitle}</P>
						</FormLeft>
						<FormCenter>
							<ImageWrapper>
								<img src={bannerForm?.bannerImage?.file?.url} alt="guide" />
							</ImageWrapper>
						</FormCenter>
						<FormRight>
							<FormRightContainer>
								{bannerForm.contentfulfields.map(field => {
									const { id, fieldName, label } = field
									return (
										<FormField
											ref={register}
											key={id}
											error={errors[fieldName]}
											hideLabel
											placeholder={label}
											customStyle={customInputStyles}
											{...field}
										/>
									)
								})}
								{bannerForm.privacyConsent ? (
									<PrivacyConsent>
										<Checkbox
											name="termsAccepted"
											ref={register}
											labelInnerHTML={{ __html: PRIVACY_AND_POLICY }}
											data-invalid={Boolean(errors.termsAccepted)}
											customStyles={css`
											display: flex;
											justify-content: center;
										`}
										/>
										{errors.termsAccepted ? (
											<ErrorLabel>
												<span>{errors.termsAccepted.message}</span>
											</ErrorLabel>
										) : null}
									</PrivacyConsent>
								) : null}
								<Button css={customButtonStyles} label={bannerForm.submitText} type="submit" variant="secondary-white" />
							</FormRightContainer>
						</FormRight>

						{submitedMessage ? (
							<MessageOverlay>
								<MessageOverlayIcon />
								<MessageOverlaySeparator />
								<MessageOverlayText
									dangerouslySetInnerHTML={{
										__html: submitedMessage.message,
									}}
								/>
							</MessageOverlay>
						) : null}
					</FormBase>
				</SectionThemeContext.Provider>
			) : null}
		</>
	)
}

export default React.memo(BlogPost)
